import React, { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import DateTimeRangeFilterItem from '../../pages/delivery/components/DateTimeRangeFilterItem';
import HistoryParcelLink from './HistoryParcelLink';
import { HistoryParcelSystem } from './historyLink';
import {
    DateTimeRangeValue,
    isDateBeforeEarliest,
    isDeliveredAtOutOfRange,
    normalizeDeliveredAt,
} from './deliveredAtRange';
import './parcelQueryRange.css';

const DISPLAY_FORMAT = 'YYYY/MM/DD HH:mm';

interface DeliveredAtRangeFieldProps {
    label: string;
    value: DateTimeRangeValue;
    onChange: (dates: [string | null, string | null] | null) => void;
    spanClassName?: string;
    /** 包裹查询覆盖天数；null（配置未加载）时不限制、不展示说明 */
    days: number | null;
    system: HistoryParcelSystem;
    /** 当前表单筛选条件，「历史包裹查询」链接据此回填（R5） */
    filters: Record<string, any>;
    /** 输入框格式，默认精确到分钟；与所在页面其它时间筛选保持一致 */
    format?: string;
}

/**
 * 签收时间筛选。
 *
 * - R2：输入框下方常驻范围说明 + 「历史包裹查询」链接；
 * - R3：日期面板早于「当前时间 − N 天」的日期置灰、不可点；选择 / 手动输入（回车、失焦时 RangePicker 触发 onChange）后校验：
 *   仅开始时间超范围 → 自动调整为可选最早时间，说明变橙色，再次修改后恢复；
 *   开始、结束都超范围 → 不调整，输入框标红、说明变红色；查询按钮由父组件用 isDeliveredAtOutOfRange 禁用。
 * 默认值不变（签收时间默认为空），只有填了签收时间才做限制。
 *
 * 为什么不用 RangePicker 的 disabledDate：它同时用于校验手动输入，命中的输入会被当作非法值丢弃（不触发 onChange），
 * 且焦点会锁在该输入框、点不到结束框，导致 PRD 的状态 B（橙色调整）、状态 C（红色禁止查询）永远触发不了。
 * 所以面板置灰改用 cellRender 标记 + CSS 禁止点击（见 parcelQueryRange.css），手动输入照常进入 onChange 再由本组件校验。
 */
const DeliveredAtRangeField: React.FC<DeliveredAtRangeFieldProps> = ({
    label,
    value,
    onChange,
    spanClassName,
    days,
    system,
    filters,
    format = DISPLAY_FORMAT,
}) => {
    const withSeconds = format.includes(':ss');
    const { t } = useTranslation();
    const [adjustedStart, setAdjustedStart] = useState<string | undefined>(undefined);

    const handleChange = (dates: [string | null, string | null] | null) => {
        const normalized = normalizeDeliveredAt(dates, days);
        setAdjustedStart(normalized.adjustedStart);
        onChange((normalized.value as [string | null, string | null] | null) ?? null);
    };

    // 日期面板：超范围的日期格置灰、不可点（只作用于面板，不影响手动输入）
    const cellRender = (current: Dayjs | number | string, info: { type: string; originNode: React.ReactElement }) => {
        if (info.type === 'date' && dayjs.isDayjs(current) && isDateBeforeEarliest(current, days)) {
            return <div className="pqr-cell-out-of-range">{info.originNode}</div>;
        }
        return info.originNode;
    };

    const outOfRange = isDeliveredAtOutOfRange(value, days);
    // 外部改了值（如重置）后不再显示调整提示
    const showAdjusted = !outOfRange && !!adjustedStart && !!value && value[0] === adjustedStart;

    let hint: React.ReactNode = null;
    if (days != null) {
        if (outOfRange) {
            hint = (
                <div className="pqr-hint pqr-hint--error">
                    {t('parcelQueryRange.deliveredAtOutOfRange', { days })}{' '}
                    <HistoryParcelLink system={system} filters={filters} source="deliveredAtOutOfRange" />
                </div>
            );
        } else if (showAdjusted) {
            hint = (
                <div className="pqr-hint pqr-hint--adjusted">
                    {t('parcelQueryRange.deliveredAtAdjusted', { days, time: dayjs(adjustedStart).format(DISPLAY_FORMAT) })}{' '}
                    <HistoryParcelLink system={system} filters={filters} source="deliveredAtAdjusted" />
                </div>
            );
        } else {
            hint = (
                <div className="pqr-hint">
                    {t('parcelQueryRange.deliveredAtHint', { days })}{' '}
                    <HistoryParcelLink system={system} filters={filters} source="deliveredAtHint" />
                </div>
            );
        }
    }

    return (
        <DateTimeRangeFilterItem
            label={label}
            value={value}
            onChange={handleChange}
            spanClassName={spanClassName}
            format={format}
            showTime={withSeconds
                ? { format: 'HH:mm:ss', defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')] }
                : { format: 'HH:mm', defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')] }}
            cellRender={days != null ? cellRender : undefined}
            status={outOfRange ? 'error' : undefined}
            extra={hint}
        />
    );
};

export default DeliveredAtRangeField;
